import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Card, ConfirmModal, LanguagePicker, Modal, isoToVN } from '@rs/ui'
import { USER_ADMIN_ROLES, USER_PASSWORD_RESET_ROLES } from '@rs/policy/routes'
import { useAuth } from '../../app/auth.tsx'
import { invalidate, messageOf, qk, useList, useOne } from '../../shared/queries.ts'
import { fetchAppUsers, type AppUserRow } from '../catalog/users-api.ts'
import { ChangeOwnPasswordForm } from './ChangeOwnPasswordForm.tsx'
import { fetchPasswordState, resetUserPasswordApi } from './password-api.ts'

/**
 * ══ 🔒 MẬT KHẨU ĐĂNG NHẬP (E13 sửa 19/10/2026) — chép `proship-trans/src/components/PasswordCard.tsx` ══════════════
 *
 * Bản cũ: MỘT thẻ ở Cài đặt, hai việc — ① mọi người đổi mật khẩu của chính mình; ② admin / KTT 🔑 cấp lại mật khẩu tạm
 * (đọc cho người dùng), lần vào sau bị bắt đặt lại. rs-erp giữ đúng hai việc đó, thêm hai chỗ:
 *   · `PasswordButton` — nút 🔒 ở đầu trang mở cùng form ①: `/settings` chỉ mở cho vai văn phòng (`RESTRICTED_ROUTES`),
 *     nên sale / TPKD không có đường nào khác để đặt mật khẩu cho "Đăng nhập với tài khoản".
 *   · `ForcedPasswordPage` — màn ÉP đổi khi đang dùng mật khẩu tạm (bản cũ `Login.tsx:309` hộp "Đặt mật khẩu" không có nút
 *     bỏ qua). Máy chủ cũng chặn mọi cửa khác (`plugins/authorize.ts`), màn này chỉ là lối đi cho người dùng.
 * Chốt bảo mật giữ thêm so với bản cũ nằm ở máy chủ (`@rs/identity.passwordResetError`); danh sách ② ở đây chỉ ẩn người
 * mà máy chủ sẽ từ chối cho đỡ bấm hụt.
 */

/** Dòng tình trạng "đã đặt ngày … / chưa đặt" — bản cũ `PasswordCard.tsx:99-103`. */
const PasswordStateLine = ({ passSet, passSetAt }: { passSet: boolean; passSetAt: string | null }): JSX.Element => {
  const { t } = useTranslation()
  return passSet
    ? <p className="text-[11px] text-slate-500">{t('login.pw.stateSet', { date: passSetAt === null ? '' : isoToVN(passSetAt.slice(0, 10)) })}</p>
    : <p className="text-[11px] font-semibold text-amber-600">{t('login.pw.stateNone')}</p>
}

/** Phần ① — tình trạng + form. Dùng chung thẻ ở Cài đặt và hộp 🔒 ở đầu trang. */
const OwnPassword = (): JSX.Element => {
  const { t } = useTranslation()
  const st = useOne(qk.passwordState(), fetchPasswordState)
  const [done, setDone] = useState(false)
  if (st.err !== null) return <p role="alert" className="text-sm text-rose-700">{st.err}</p>
  if (st.data === null) return <p className="text-sm text-slate-500">{t('common.loading')}</p>
  return (
    <div className="space-y-3">
      <PasswordStateLine passSet={st.data.passSet} passSetAt={st.data.passSetAt} />
      {done && <p role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[13px] text-emerald-700">{t('login.pw.done')}</p>}
      <ChangeOwnPasswordForm passSet={st.data.passSet} onDone={() => { setDone(true); st.reload() }} />
    </div>
  )
}

/** Huy hiệu trạng thái từng người — bản cũ `PasswordCard.tsx:150-154`. */
const StateBadge = ({ u }: { u: AppUserRow }): JSX.Element => {
  const { t } = useTranslation()
  const base = 'rounded border px-1.5 py-0.5 text-[10px] font-bold'
  if (u.mustChangePass) return <span className={`${base} border-amber-200 bg-amber-50 text-amber-600`}>{t('login.pw.badgeTemp')}</span>
  if (u.passSet) return <span className={`${base} border-emerald-200 bg-emerald-50 text-emerald-600`}>{t('login.pw.badgeSet')}</span>
  return <span className={`${base} border-slate-200 bg-slate-50 text-slate-400`}>{t('login.pw.badgeNone')}</span>
}

/** Phần ② — admin / KTT cấp lại mật khẩu tạm. */
const ResetList = ({ meId, meRole }: { meId: string; meRole: string }): JSX.Element => {
  const { t } = useTranslation()
  const client = useQueryClient()
  const { rows, err } = useList(qk.appUsers(), fetchAppUsers)
  const [asking, setAsking] = useState<AppUserRow | null>(null)
  const [issued, setIssued] = useState<{ name: string; tempPassword: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [actErr, setActErr] = useState<string | null>(null)
  /* Ẩn đúng những người máy chủ sẽ từ chối (`passwordResetError`): chính mình · người đang khoá · admin/GĐ khi người cấp
     là KTT. Bản cũ cũng chỉ liệt kê người đang hoạt động (`PasswordCard.tsx:144`). */
  const managerActor = (USER_ADMIN_ROLES as readonly string[]).includes(meRole)
  const list = (rows ?? []).filter((u) => u.active && u.id !== meId
    && (managerActor || !(USER_ADMIN_ROLES as readonly string[]).includes(u.role)))

  const doReset = async (u: AppUserRow): Promise<void> => {
    setBusy(true); setActErr(null)
    try {
      const r = await resetUserPasswordApi(u.id)
      setAsking(null)
      setIssued({ name: u.name, tempPassword: r.tempPassword })
      invalidate(client, qk.appUsers())
    } catch (x) {
      setActErr(messageOf(x))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="border-t border-slate-100 pt-4">
      <div className="mb-1 text-sm font-semibold text-slate-700">{t('login.pw.resetTitle')}</div>
      <p className="mb-2 text-[11px] text-slate-500">{t('login.pw.resetHint')}</p>
      {err !== null && <p role="alert" className="text-sm text-rose-700">{err}</p>}
      <div className="max-h-72 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
        {list.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
            <div className="min-w-0">
              <div className="truncate font-semibold text-slate-700">{u.name}</div>
              <div className="truncate text-slate-400">{u.email}</div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <StateBadge u={u} />
              <button type="button" disabled={busy} onClick={() => { setActErr(null); setAsking(u) }}
                      className="text-[11px] font-semibold text-sky-600 hover:underline disabled:opacity-40">
                {t('login.pw.resetBtn')}
              </button>
            </div>
          </div>
        ))}
      </div>
      {asking !== null && (
        <ConfirmModal
          title={t('login.pw.resetTitle')}
          message={t('login.pw.resetConfirm', { name: asking.name, email: asking.email })}
          busy={busy} error={actErr}
          onConfirm={() => { void doReset(asking) }}
          onClose={() => { setAsking(null) }}
        />
      )}
      {/* Mật khẩu tạm hiện ĐÚNG MỘT LẦN — đóng hộp là mất, không cất vào kho truy vấn hay `localStorage`. */}
      {issued !== null && (
        <Modal title={t('login.pw.resetTitle')} onClose={() => { setIssued(null) }}>
          <p className="text-sm text-slate-700">{t('login.pw.resetDone', { name: issued.name })}</p>
          <p className="mt-2 select-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center font-mono text-lg font-bold tracking-wider text-slate-800"
             data-testid="mat-khau-tam">
            {issued.tempPassword}
          </p>
        </Modal>
      )}
    </div>
  )
}

/** Thẻ ở `/settings` — bản cũ đặt NGAY TRÊN thẻ thông tin công ty (`Settings.tsx:37`). */
export const PasswordCard = (): JSX.Element | null => {
  const { t } = useTranslation()
  const { me } = useAuth()
  if (me === null) return null
  const canReset = (USER_PASSWORD_RESET_ROLES as readonly string[]).includes(me.role)
  return (
    <Card className="space-y-4 p-5">
      <div>
        <div className="text-sm font-bold text-slate-700">{t('login.pw.cardTitle')}</div>
        <p className="mt-0.5 text-[11px] text-slate-500">{me.name} · {me.email}</p>
      </div>
      <OwnPassword />
      {canReset && <ResetList meId={me.id} meRole={me.role} />}
    </Card>
  )
}

/** Nút 🔒 ở đầu trang — mở form ① cho MỌI vai nội bộ (kể cả vai không vào được `/settings`). */
export const PasswordButton = (): JSX.Element => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button type="button" onClick={() => { setOpen(true) }} title={t('login.pw.cardTitle')} data-testid="nut-mat-khau"
              className="rounded-lg bg-white/20 px-2.5 py-1.5 text-xs font-semibold hover:bg-white/30">
        {t('login.pw.button')}
      </button>
      {open && (
        <Modal title={t('login.pw.cardTitle')} onClose={() => { setOpen(false) }}>
          <OwnPassword />
        </Modal>
      )}
    </>
  )
}

/**
 * Màn ÉP đổi mật khẩu tạm — router vẽ nó thay cho MỌI màn khi `me.mustChangePass` (bản cũ `Login.tsx:309`: không nút bỏ
 * qua, chỉ có đăng xuất). Đổi xong thì hỏi lại `/auth/me` ⇒ cờ tắt ⇒ router vẽ đúng màn người dùng đang mở.
 */
export const ForcedPasswordPage = (): JSX.Element => {
  const { t } = useTranslation()
  const { me, refresh, logout } = useAuth()
  const client = useQueryClient()
  return (
    <main className="min-h-full grid place-items-center bg-gradient-to-br from-brand-50 via-white to-sky-100 p-4">
      <div className="w-full max-w-md">
        <header className="mb-3 flex justify-end">
          <LanguagePicker tone="light" />
        </header>
        <div className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-brand-500 to-sky-500 px-6 py-7 text-center">
            <img src="/RT-LOGO-header.jpg" alt={t('app.name')}
                 className="mx-auto mb-2 h-12 w-auto rounded-lg bg-white object-contain px-2 py-1" />
            <div className="text-sm text-brand-50">{me?.name} · {me?.email}</div>
          </div>
          <div className="space-y-4 px-6 py-6">
            <h1 className="text-lg font-bold text-slate-800">{t('login.pw.forcedTitle')}</h1>
            <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[13px] text-amber-800">
              {t('login.pw.forced')}
            </p>
            {/* Đang dùng mật khẩu tạm ⇒ chắc chắn ĐÃ có mật khẩu: ô "Mật khẩu hiện tại" = mật khẩu tạm vừa gõ. */}
            <ChangeOwnPasswordForm passSet onDone={() => { invalidate(client, qk.passwordState()); void refresh() }} />
            <button type="button" onClick={() => { void logout() }}
                    className="text-xs font-semibold text-slate-500 hover:underline">
              {t('common.logout')}
            </button>
          </div>
        </div>
      </div>
    </main>
  )
}
